import { Button, Form, Grid, Input, InputNumber, Select, Space, Table } from "@arco-design/web-react";
import { copy } from "../utils/copy";
import { IconCopy } from "@arco-design/web-react/icon";
import React, { useEffect, useState } from "react";
import { check } from "../utils/check";
import { toHValue } from "../utils/wrap";
import { usePasswordCheck } from "../hooks/usePasswordCheck";

const FormItem = Form.Item;
const Option = Select.Option;

const LinearSpeedCalculation = ({ onPasswordInvalid }) => {
  const { checkPasswordBeforeAction } = usePasswordCheck(onPasswordInvalid);
  const [form] = Form.useForm();
  const [current, setCurrent] = useState(1); // 1..10 steps, 11 = 数据分析
  const [btnState, setBtnState] = useState({ up: false, down: false, reset: false });

  // localStorage persistence helpers
  const STORAGE_KEY = "mdt_values_v1";
  const loadAll = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) {
      return {};
    }
  };
  const saveAll = (obj) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
    } catch (e) {
      // ignore quota or JSON errors
    }
  };
  const getStepKeys = (idx) => [`d${idx}`, `ud${idx}`, `dd${idx}`, `md${idx}`, `tr${idx}`, `ad${idx}`, `m${idx}`];
  const getStepValuesFromStore = (idx, store) => {
    const data = {};
    getStepKeys(idx).forEach((k) => (data[k] = store[k]));
    return data;
  };
  const setStepValuesToForm = (idx, store) => {
    const payload = getStepValuesFromStore(idx, store ?? loadAll());
    form.setFieldsValue(payload);
  };
  const persistCurrentFormToStore = (idx) => {
    const store = loadAll();
    const values = form.getFieldsValue();
    getStepKeys(idx).forEach((k) => {
      store[k] = values[k];
    });
    saveAll(store);
  };

  // initial restore for step 1
  useEffect(() => {
    setStepValuesToForm(1);
    const s = loadAll();
    const d = s[`d1`];
    const ud = s[`ud1`];
    const dd = s[`dd1`];
    const ad = s[`ad1`];
    setBtnState({
      up: d !== undefined && ud !== undefined && dd !== undefined,
      down: ad !== undefined && d !== undefined && ud !== undefined && dd !== undefined,
      reset: getStepKeys(1).some((k) => s[k] !== undefined),
    });
  }, []);

  const calculate = () => {
    checkPasswordBeforeAction(() => {
      if (current >= 1 && current <= 10) {
        const d = form.getFieldValue(`d${current}`);
        const ud = form.getFieldValue(`ud${current}`);
        const dd = form.getFieldValue(`dd${current}`);
        const ad = form.getFieldValue(`ad${current}`);
        if (d !== undefined && ud !== undefined && dd !== undefined) {
          const md = d + (ud + dd) / 2;
          const tr = Math.abs(ud - dd) / 2;
          form.setFieldValue(`md${current}`, toHValue(md));
          form.setFieldValue(`tr${current}`, toHValue(tr));
          if (ad !== undefined) {
            const m = ad - md;
            form.setFieldValue(`m${current}`, toHValue(m));
          }
          // persist calculated results
          persistCurrentFormToStore(current);
        }
      }
    });
  };

  const options = Array.from({ length: 10 }, (_, i) => ({
    name: `台阶${i + 1}尺寸计算`,
    value: i + 1,
  })).concat([{ name: "数据分析", value: 11 }]);

  const onChange = (value) => {
    setCurrent(value);
    const store = loadAll();
    // restore values for selected step
    if (value >= 1 && value <= 10) {
      setStepValuesToForm(value, store);
    }
    // recalc btn state for new current based on store
    const d = store[`d${value}`];
    const ud = store[`ud${value}`];
    const dd = store[`dd${value}`];
    const ad = store[`ad${value}`];
    setBtnState({
      up: d !== undefined && ud !== undefined && dd !== undefined,
      down: ad !== undefined && d !== undefined && ud !== undefined && dd !== undefined,
      reset: getStepKeys(value).some((k) => store[k] !== undefined),
    });
  };

  const resetCurrentFields = () => {
    const fields = getStepKeys(current);
    const payload = {};
    fields.forEach((f) => (payload[f] = undefined));
    form.setFieldsValue(payload);
    // clear from storage only on reset
    const store = loadAll();
    fields.forEach((f) => delete store[f]);
    saveAll(store);
    setBtnState({ up: false, down: false, reset: false });
  };

  const renderStepForm = (idx) => (
    <>
      <FormItem
        label={`台阶${idx}尺寸 φ'`}
        field={`d${idx}`}
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber min={0} size="large" />
      </FormItem>
      <FormItem
        label="上偏差"
        field={`ud${idx}`}
        rules={[{ validator: (value, cb) => (value !== undefined ? cb() : cb("此项必填！")) }]}
      >
        <InputNumber size="large" />
      </FormItem>
      <FormItem
        label="下偏差"
        field={`dd${idx}`}
        rules={[{ validator: (value, cb) => (value !== undefined ? cb() : cb("此项必填！")) }]}
      >
        <InputNumber size="large" />
      </FormItem>
      <FormItem wrapperCol={{ offset: 5 }}>
        <Space>
          <Button type="primary" onClick={calculate} disabled={!btnState.up}>
            立即计算
          </Button>
          <Button onClick={resetCurrentFields} disabled={!btnState.reset}>
            重置
          </Button>
        </Space>
      </FormItem>
      <FormItem label="中差φ" style={{ marginBottom: 0 }}>
        <Grid.Row gutter={8}>
          <Grid.Col span={12}>
            <Form.Item field={`md${idx}`}>
              <Input
                size="large"
                id={`result_mdt_md_${idx}`}
                readOnly
                onClick={copy}
                placeholder="计算结果在此次显示"
                suffix={
                  <IconCopy onClick={copy.bind(this, null, `#result_mdt_md_${idx}`)} style={{ cursor: "pointer" }} />
                }
              />
            </Form.Item>
          </Grid.Col>
          <Grid.Col span={2}>
            <span style={{ display: "block", marginTop: "0.5rem", marginLeft: "0.5rem" }}>±</span>
          </Grid.Col>
          <Grid.Col span={10}>
            <Form.Item field={`tr${idx}`}>
              <Input
                size="large"
                id={`result_mdt_tr_${idx}`}
                readOnly
                onClick={copy}
                placeholder="公差范围"
                suffix={
                  <IconCopy onClick={copy.bind(this, null, `#result_mdt_tr_${idx}`)} style={{ cursor: "pointer" }} />
                }
              />
            </Form.Item>
          </Grid.Col>
        </Grid.Row>
      </FormItem>
      <FormItem
        label={`台阶${idx}实际尺寸 φ''`}
        field={`ad${idx}`}
        rules={[{ validator: (value, cb) => (value > 0 ? cb() : cb("此项必填且大于 0!")) }]}
      >
        <InputNumber min={0} size="large" />
      </FormItem>
      <FormItem wrapperCol={{ offset: 5 }}>
        <Space>
          <Button type="primary" onClick={calculate} disabled={!btnState.down}>
            立即计算
          </Button>
          <Button onClick={resetCurrentFields} disabled={!btnState.reset}>
            重置
          </Button>
        </Space>
      </FormItem>
      <FormItem label="余量m" field={`m${idx}`}>
        <Input
          size="large"
          id={`result_mdt_m_${idx}`}
          readOnly
          onClick={copy}
          placeholder="计算结果在此次显示"
          suffix={<IconCopy onClick={copy.bind(this, null, `#result_mdt_m_${idx}`)} style={{ cursor: "pointer" }} />}
        />
      </FormItem>
    </>
  );
  console.log(current);

  const renderAnalysis = () => {
    const rows = [];
    // use persisted values instead of transient form store
    const all = loadAll();
    const mdArr = [];
    const mArr = [];
    for (let i = 1; i <= 10; i++) {
      const d = all[`d${i}`];
      const ud = all[`ud${i}`];
      const dd = all[`dd${i}`];
      const ad = all[`ad${i}`];
      let md = undefined;
      if (d !== undefined && ud !== undefined && dd !== undefined) {
        md = d + (ud + dd) / 2;
      }
      const m = ad !== undefined && md !== undefined ? ad - md : undefined;
      mdArr[i] = md;
      mArr[i] = m;
      rows.push({
        key: i,
        idx: i,
        ad,
        md,
        m,
        mp: undefined, // to be computed below
      });
    }
    const baseM = mArr[1];
    for (let i = 1; i <= 10; i++) {
      rows[i - 1].mp = mArr[i] !== undefined && baseM !== undefined ? baseM - mArr[i] : undefined;
    }

    const columns = [
      { title: "台阶项目", dataIndex: "idx", width: 50 },
      {
        title: "实际尺寸 φ''",
        dataIndex: "ad",
        width: 120,
        render: (val) => <Input readOnly value={val === undefined ? "" : toHValue(val)} />,
      },
      {
        title: "中差 φ",
        dataIndex: "md",
        width: 120,
        render: (val) => <Input readOnly value={val === undefined ? "" : toHValue(val)} />,
      },
      {
        title: "余量 m",
        dataIndex: "m",
        width: 120,
        render: (val) => <Input readOnly value={val === undefined ? "" : toHValue(val)} />,
      },
      {
        title: "直径差调整量",
        dataIndex: "mp",
        width: 120,
        render: (val) => <Input readOnly value={val === undefined ? "" : toHValue(val)} />,
      },
    ];

    const scrollX = columns.reduce((sum, col) => sum + (col.width || 0), 0);

    return (
      <div style={{ overflowX: "auto" }}>
        <Table columns={columns} data={rows} pagination={false} size="small" scroll={{ x: scrollX }} />
      </div>
    );
  };

  return (
    <div className="cpn-wrapper-horizontal">
      <Form
        style={{ maxWidth: 700, width: "95%" }}
        autoComplete="off"
        form={form}
        colon=":"
        layout={document.body.clientWidth <= 780 ? "vertical" : "horizontal"}
        onValuesChange={(changedValues, values) => {
          // persist only current step values, keep others in store
          const store = loadAll();
          getStepKeys(current).forEach((k) => {
            if (k in values) store[k] = values[k];
          });
          saveAll(store);

          // recalc btn state for current step
          const d = values[`d${current}`];
          const ud = values[`ud${current}`];
          const dd = values[`dd${current}`];
          const ad = values[`ad${current}`];
          setBtnState({
            up: d !== undefined && ud !== undefined && dd !== undefined,
            down: ad !== undefined && d !== undefined && ud !== undefined && dd !== undefined,
            reset: getStepKeys(current).some((k) => values[k] !== undefined || store[k] !== undefined),
          });
        }}
      >
        <FormItem label={`类型`} field="type">
          <Select size="large" placeholder="请选择类型" onChange={onChange} defaultValue={1} value={current}>
            {options.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.name}
              </Option>
            ))}
          </Select>
        </FormItem>

        {current >= 1 && current <= 10 && renderStepForm(current)}
        {current === 11 && renderAnalysis()}
      </Form>
    </div>
  );
};
export default LinearSpeedCalculation;
